import * as React from "react";

/**
 * Shared rendering primitive for the deal note / activity threads (CHP-551).
 *
 * The three thread surfaces — Deal Owner Notes (DealNotesSection), Internal
 * notes (DealInternalNotesSection, CM/RA/RM-only), and the Deal Progress /
 * activity log (DealProgressSection) — all duplicated the same list shell:
 * loading copy, empty-state copy, the `<ul>`/`<li>` row layout, the
 * author·timestamp header line, and the `whitespace-pre-wrap` body. Their data
 * sources, permission gating, headings and composers differ and stay owned by
 * each component; only the read-side row/list markup is consolidated here.
 *
 * Each row is parameterised: callers supply the body via `children` (a plain
 * note paragraph, an inline-edit form, or structured activity fields) and an
 * optional right-aligned `headerAction` (an Edit button or a context badge).
 */

/** DD/MM/YYYY, HH:MM (Australian). Single source for the three note threads. */
export function formatTimestamp(value: string): string {
  return new Date(value).toLocaleString("en-AU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * One thread entry row: the bordered `<li>` with an author·timestamp header line
 * (plus an optional right-aligned action slot) above caller-supplied body
 * content.
 */
export function NoteEntry({
  author,
  timestamp,
  edited = false,
  headerAction,
  children,
}: {
  /** Display name of the note's author/actor; falls back to "Unknown". */
  author: string | null | undefined;
  /** ISO timestamp string for the note's creation. */
  timestamp: string;
  /** Append " (edited)" to the header line (plain notes only). */
  edited?: boolean;
  /** Right-aligned slot in the header line (e.g. an Edit button or a badge). */
  headerAction?: React.ReactNode;
  /** The note body — plain text, an inline-edit form, or structured fields. */
  children: React.ReactNode;
}) {
  return (
    <li className="rounded-md border border-border p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium text-muted-foreground">
          {author ?? "Unknown"} · {formatTimestamp(timestamp)}
          {edited ? " (edited)" : ""}
        </p>
        {headerAction ?? null}
      </div>
      {children}
    </li>
  );
}

/**
 * The thread shell: renders a loading line, an error line, an empty-state line,
 * or the list of entries. `entries` is the already-prepared (and, where
 * relevant, reversed) list; `renderEntry` maps each to a {@link NoteEntry}.
 *
 * CHP-804: an `isError` branch is essential here — a failed fetch previously
 * fell through to `data ?? []` and rendered the empty-state line ("No notes
 * yet."), so a load failure read as "none". For internal notes this is
 * decision-critical (the closed-won rejection reason is persisted as an
 * internal note), so the error state is distinct, never the empty line.
 */
export function NoteThread<T>({
  entries,
  isLoading,
  isError,
  loadingLabel,
  emptyLabel,
  errorLabel = "Couldn't load. Please try again.",
  onRetry,
  renderEntry,
}: {
  entries: T[];
  isLoading: boolean;
  isError?: boolean;
  loadingLabel: string;
  emptyLabel: string;
  errorLabel?: string;
  onRetry?: () => void;
  renderEntry: (entry: T) => React.ReactNode;
}) {
  if (isLoading) {
    return <p className="text-sm text-muted-foreground">{loadingLabel}</p>;
  }
  if (isError) {
    return (
      <div role="alert" className="text-sm text-destructive">
        <p>{errorLabel}</p>
        {onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="mt-1 text-sm font-medium underline underline-offset-2"
          >
            Retry
          </button>
        ) : null}
      </div>
    );
  }
  if (entries.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyLabel}</p>;
  }
  return <ul className="space-y-3">{entries.map(renderEntry)}</ul>;
}

/** Plain note body — the default `whitespace-pre-wrap` paragraph. */
export function NoteBody({ children }: { children: React.ReactNode }) {
  return <p className="mt-1 whitespace-pre-wrap text-sm">{children}</p>;
}
